import React, { useEffect, useState } from "react";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { Box, Stack, Theme } from "@mui/material";
import { FilterModal } from "../FilterModal";
import { DatePicker } from "../../components/calender";
import { FilterItemButton } from "../../components/Button/FilterItemButton";

import { ClientResponse, IQueryPerams } from "../../config/types";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import ModalItemSelectInput from "../../components/Input/ModalItemSelectInput";
import {
  ASSET_STATUS_LIST,
  AssetDistributionStatusType,
  assetDistributionStatusList,
} from "../../config/constants/common";
import CustomizedSwitch from "../../components/Switch";
import { formatSnakeCaseToReadable } from "../../utils/common";
import { AssetCategorySelectInput } from "../select-input-models/AssetCategorySelectInput";
import { Field } from "../../utils/formSchema";
import ItemSelectInput from "../../components/Input/ItemSelectInput";

interface FilterProps<T> {
  ftn: (params: IQueryPerams) => Promise<ClientResponse<T>>;
  updateQuerySet: (query: React.SetStateAction<IQueryPerams>) => void;
  updateDataSet: (data: T) => void;
  isDeleted?: boolean;
  isAssigned?: boolean;
  distribution_status?: AssetDistributionStatusType;
}

interface Ifilter {
  batch_number: string;
  model: string;
  asset_status: string;
  asset_category_name: string;
  is_assigned?: boolean;
  is_deleted?: boolean;
  brand: string;
  date_received: string;
  distribution_status?: AssetDistributionStatusType;
}
const assetDistributionStatus = assetDistributionStatusList.map((item) => ({
  label: item,
  value: item,
}));

export const AssetLogsFilter = <T,>({
  ftn,
  updateQuerySet,
  updateDataSet,
  isAssigned,
  isDeleted,
  distribution_status,
}: FilterProps<T>) => {
  const [loading, setLoading] = useState(true);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<Ifilter>({
      batch_number: "",
      model: "",
      asset_status: "",
      asset_category_name: "",
      brand: "",
      date_received: "",
      is_assigned: isAssigned,
      is_deleted: isDeleted,
    });
  const handleDatePicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      received_date: { value: date, isValid: true },
    }));
  };

  const resetFilter = <K extends keyof Ifilter>(key: K) => {
    updateForm((p) => {
      const value = typeof p[key]?.value === "boolean" ? false : "";
      const state = {
        ...p,
        [key]: { value },
      };
      updateQuerySet((prev) => ({ ...prev, [key]: value }));
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    const new_data = {
      ...data,
      is_assigned: isAssigned === false ? false : data.is_assigned,
    };
    updateQuerySet(data);
    const filterRequest = async () => {
      setLoading(true);
      const res = await ftn(new_data);
      if (res.data && updateDataSet) {
        updateDataSet(res.data);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.batch_number.value,
    form.date_received.value,
    form.brand.value,
    form.is_assigned?.value,
    form.is_deleted?.value,
    form.model,
    form.asset_category_name,
    form.distribution_status?.value,
  ]);

  function handleAssetCatSelect(
    item: Field,
    isPhysical?: boolean,
    label?: string
  ) {
    updateForm((p) => ({ ...p, asset_category_name: { value: label ?? "" } }));
  }
  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      width={"100%"}
      mb={1}
    >
      <Stack direction={"row"} spacing={2}>
        <FilterModal label="Filters" loading={loading}>
          <Stack direction={"column"} spacing={2}>
            <DatePicker
              handleOnchange={handleDatePicker}
              label="Date Recieved"
            />
            <ItemSelectInput
              size="full"
              label={"Asset Distribution Status"}
              item={""}
              placeholder={"Search by asset distribution status ..."}
              items={assetDistributionStatus}
              handleChange={updateInputField("distribution_status")}
              value={form.distribution_status?.value ?? ""}
            />
            <AssetCategorySelectInput
              placeholder={"Select Asset Category"}
              label={"Asset Category"}
              handleChange={handleAssetCatSelect}
              size="full"
            />

            <SideBarSearchInput
              label="Model Name"
              placeholder="Search by model name..."
              value={form.model.value}
              handleChange={updateInputField("model")}
            />
            <SideBarSearchInput
              label="Batch Number"
              placeholder="Search by batch number..."
              value={form.batch_number.value}
              handleChange={updateInputField("batch_number")}
            />
            <ModalItemSelectInput
              label="Asset Condition"
              placeholder="Search by asset condition"
              items={ASSET_STATUS_LIST}
              item=""
              value={form.asset_status.value}
              handleChange={updateInputField("asset_status")}
            />
            {isAssigned !== false && (
              <CustomizedSwitch
                title="Assigned"
                onChange={updateInputField("is_assigned", "BOOLEAN")}
                value={form.is_assigned?.value ?? false}
              />
            )}
            {isDeleted !== false && (
              <CustomizedSwitch
                title="Deleted"
                onChange={updateInputField("is_deleted", "BOOLEAN")}
                value={form.is_deleted?.value ?? false}
              />
            )}
          </Stack>
        </FilterModal>
        <Box sx={styles.filterLayout}>
          {Object.entries(getFormValues())
            .filter(
              ([_, value]) =>
                (typeof value === "string" && value.trim() !== "") ||
                typeof value === "boolean"
            )
            .map(([key, value]) => (
              <FilterItemButton
                key={key}
                label={`By ${formatSnakeCaseToReadable(key)}`}
                tip={value.toString()}
                height={40}
                onClick={() => resetFilter(key as keyof Ifilter)}
              />
            ))}
        </Box>
      </Stack>
      <Box width={250}>
        <SideBarSearchInput
          placeholder="Search by brand name..."
          value={form.brand.value}
          handleChange={updateInputField("brand")}
        />
      </Box>
    </Stack>
  );
};

const styles = {
  filterLayout: (theme: Theme) => ({
    width: 1000,
    [theme.breakpoints.down("xl")]: {
      width: 570,
    },
  }),
};
